import { TwoColumnLayout, Media } from '@pelotoncycle/design-system';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { track } from '@peloton/analytics';
import { getCurrentRelativePathname } from '@peloton/internationalize/models/path';
import { media } from '@peloton/styles';
import type { UserTrackingProperties } from '@ecomm/auth/selectors';
import { getUserTrackingProperties } from '@ecomm/auth/selectors';
import type {
  CtaWithUrl,
  HeaderBodyImage,
  KeyValue,
  LegalText,
  CtaWithUrlData,
} from '@ecomm/copy';
import {
  useHeaderBodyImageData,
  useKeyValueData,
  useCtaWithUrlData,
  useLegalTextData,
} from '@ecomm/copy';
import { TypographyCtaHeadlineListSupport } from '@ecomm/membership';
import Modal from './Modal';
import { ModalWidth } from './ModalView';

type TwoColumnModalProps = {
  closeHandler: () => void;
  openHandler: () => void;
  contentLabel: string;
  ctaId: CtaWithUrl;
  headerBodyImageId: HeaderBodyImage;
  legalId: LegalText;
  listId: KeyValue;
};

const TwoColumnModal: React.FC<React.PropsWithChildren<TwoColumnModalProps>> = props => {
  const { header, image } = useHeaderBodyImageData(props.headerBodyImageId);

  const listKeyValue = useKeyValueData(props.listId);
  const list = listKeyValue?.split('-').filter(f => !!f) || [];

  const cta = useCtaWithUrlData(props.ctaId);

  const legal = useLegalTextData(props.legalId);

  const userTrackingProperties = useSelector(getUserTrackingProperties);

  return (
    <Modal
      contentLabel={props.contentLabel}
      closeHandler={props.closeHandler}
      openHandler={props.openHandler}
      padded={false}
      width={ModalWidth.Wide}
    >
      <TwoColumnLayout template="fiftyFifty">
        <Media
          media={{
            mobile: image.mobile.url,
            tablet: image.mobile.url,
            desktop: image.desktop.url,
            alt: image.mobile.description,
            type: 'image',
          }}
          height="auto"
        />
        <TypographyWrapper>
          <StyledTypography
            headline={header}
            list={list}
            cta={cta}
            supportText={legal}
            onClickCTA={() =>
              clickHandler(cta, userTrackingProperties, props.closeHandler)
            }
          />
        </TypographyWrapper>
      </TwoColumnLayout>
    </Modal>
  );
};

export default TwoColumnModal;

export const clickHandler = (
  cta: CtaWithUrlData,
  userTrackingProperties: UserTrackingProperties,
  closeHandler: () => void,
) => {
  track({
    event: 'Clicked Link',
    properties: {
      parentType: 'modal',
      parent: 'Modal with CTA',
      unitName: cta.key,
      linkName: cta.text,
      linkTo: cta.url,
      page: getCurrentRelativePathname(),
      isLoggedIn: userTrackingProperties.isLoggedIn,
      hasDeviceSubscription: userTrackingProperties.hasDeviceSubscription,
    },
  });
  closeHandler();
};

const StyledTypography = styled(TypographyCtaHeadlineListSupport)`
  ${media.tablet`
    max-width: 480px;
  `}

  ${media.desktopLarge`
    max-width: 402px;
  `}
`;

const TypographyWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 32px;
  text-align: left;

  ul {
    margin-top: 24px;
  }

  ${media.tablet`
    padding: 64px 80px;
  `}

  ${media.desktopLarge`
    padding: 100px 32px;
    display: flex;
    align-items: center;
  `}
`;
